import { parseIsoInstant } from './timeGrid'
import { miniBarsMarginMs } from './historyView'

// ComparisonView (story 211, PO 2026-10-17, maqueta M:1054-1080): the «before and after» of a correction drawn as timelines,
// one lane per record line it touched, with a «Before» and an «After» bar on the same scale and the corrected stretch shaded.
// Built ONLY from the trace of the detail (the APPLY rows with their before/after facts): Sun does not give the rest of the
// timeline in the detail, and the PO chose exactness over context. A close and its trim are merged: per record, «before» is
// the image before the close and «after» the image after the trim.

const HOUR = 3600000
// Longer than this, the scale is the corrected stretch with its margin (a record open for months would squash everything).
const MAX_NATURAL_RANGE_MS = 72 * HOUR

// The lanes, in the order of the maqueta: presence and clock-ins of the corrected worker, the jobs, the other workers, the
// machine state. JOB_REFERENCE and FABRICATION_ORDER_PROGRESS have no stretch to draw.
const LANE_ORDER = ['DEVICE_WORKER', 'DEVICE_WORKER_FABRICATION_ORDER', 'DEVICE_FABRICATION_ORDER', 'WORKER_OTHER_TASK', 'DEVICE_STATUS']

const msOf = (instant) => (instant ? parseIsoInstant(instant).getTime() : Number.NaN)

/** The colour of a record by its table and state, the tones of the timeline. */
export const toneOf = (table, stateCode) => {
  if (table === 'DEVICE_WORKER') return 'teal'
  if (table === 'DEVICE_STATUS') return stateCode === 'WORKING' ? 'blue' : 'grey'
  if (table === 'DEVICE_WORKER_FABRICATION_ORDER') return stateCode === 'WORKING' ? 'blue' : 'grey'
  if (table === 'DEVICE_FABRICATION_ORDER') {
    if (String(stateCode || '').startsWith('ACTIVE')) return 'blue'
    if (stateCode === 'INTERRUPTED') return 'amber'
    if (stateCode === 'FINISHED') return 'green'
    return 'grey'
  }
  return 'grey'
}

const laneKeyOf = (table, facts) => {
  if (table === 'DEVICE_STATUS') return 'DEVICE_STATUS'
  if (table === 'DEVICE_FABRICATION_ORDER') return `DEVICE_FABRICATION_ORDER-${facts.jobReferenceId}`
  return `${table}-${facts.workerId}`
}

// The name of a lane. Any id of the facts can be null (#205 §3: a clock-in inserted without worker, a job state without job):
// then the lane says so instead of «#null».
const labelOf = (table, facts, names) => {
  if (table === 'DEVICE_STATUS') return { key: 'Machine', params: {} }
  if (table === 'DEVICE_FABRICATION_ORDER') return facts.jobReferenceId ? { text: names.job(facts.jobReferenceId) } : { key: 'Without job', params: {} }
  return facts.workerId ? { text: names.worker(facts.workerId) } : { text: '—' }
}

/** The records of the traces, merged by `table:rowId`: the first before (close first) and the last after (the trim's). */
export const recordsOf = (details) => {
  const byKey = new Map()
  details.forEach((detail) => detail.rows
    .filter((row) => row.phase === 'APPLY' && LANE_ORDER.includes(row.table))
    .forEach((row) => {
      const key = `${row.table}:${row.rowId}`
      const existing = byKey.get(key)
      if (existing) existing.after = row.afterFacts
      else byKey.set(key, { key, table: row.table, before: row.beforeFacts, after: row.afterFacts })
    }))
  return Array.from(byKey.values())
}

// Instants are compared as instants, never as text (#205 §8.1): Sun may write the same instant with another precision.
const sameInstant = (a, b) => (a && b ? msOf(a) === msOf(b) : a === b)
const sameFacts = (a, b) => Boolean(a && b) && sameInstant(a.start, b.start) && sameInstant(a.end, b.end) && a.stateCode === b.stateCode

/**
 * The comparison of the details of a correction (`details` = the close first, then the chosen step or the only one), with the
 * corrected stretch `window` ({start, end}), the names of the detail (`names.worker/job/fabricationOrder`) and `nowMs` for the
 * records still open. Positions are % of the bar.
 *   { fromMs, toMs, band: {left, width}, lanes: [{ key, table, label, sub, before: [segment], after: [segment] }] }
 *   segment = { key, left, width, tone, open, changed }
 */
export const comparisonOf = ({ details, window, names, workerId, nowMs }) => {
  const records = recordsOf(details)
  const intervals = []
  records.forEach((record) => [record.before, record.after].filter(Boolean).forEach((facts) => {
    if (!facts.start) return
    intervals.push([msOf(facts.start), facts.end ? msOf(facts.end) : nowMs])
  }))
  const bandStart = msOf(window.start)
  const bandEnd = msOf(window.end)
  let fromMs = Math.min(bandStart, ...intervals.map(([start]) => start))
  let toMs = Math.max(bandEnd, ...intervals.map(([, end]) => end))
  if (toMs - fromMs > MAX_NATURAL_RANGE_MS) {
    fromMs = bandStart
    toMs = bandEnd
  }
  const margin = miniBarsMarginMs(toMs - fromMs)
  fromMs -= margin
  toMs += margin
  const length = toMs - fromMs
  const place = (startMs, endMs) => {
    const start = Math.max(fromMs, startMs)
    const end = Math.min(toMs, endMs)
    return { left: ((start - fromMs) / length) * 100, width: (Math.max(0, end - start) / length) * 100 }
  }

  const lanes = new Map()
  records.forEach((record) => {
    const owner = record.after || record.before
    // A record the close inserted and the trim deleted was never there before nor after: nothing to draw.
    if (!owner) return
    const laneKey = laneKeyOf(record.table, owner)
    if (!lanes.has(laneKey)) {
      const isJob = record.table === 'DEVICE_FABRICATION_ORDER'
      lanes.set(laneKey, {
        key: laneKey,
        table: record.table,
        workerId: owner.workerId || 0,
        label: labelOf(record.table, owner, names),
        sub: record.table === 'DEVICE_STATUS'
          ? { key: 'state', params: {} }
          : isJob
            ? { key: 'job · {{code}}', params: { code: owner.fabricationOrderId ? names.fabricationOrder(owner.fabricationOrderId) : '' } }
            : { key: { DEVICE_WORKER: 'presence', DEVICE_WORKER_FABRICATION_ORDER: 'clock-ins', WORKER_OTHER_TASK: 'Other task record' }[record.table], params: {} },
        before: [],
        after: []
      })
    }
    const lane = lanes.get(laneKey)
    const changed = !sameFacts(record.before, record.after)
    const segment = (facts, side) => {
      if (!facts || !facts.start) return
      const endMs = facts.end ? msOf(facts.end) : nowMs
      if (endMs <= fromMs || msOf(facts.start) >= toMs) return
      lane[side].push({ key: `${record.key}-${side}`, ...place(msOf(facts.start), endMs), tone: toneOf(record.table, facts.stateCode), open: !facts.end, changed })
    }
    segment(record.before, 'before')
    segment(record.after, 'after')
  })
  const order = (lane) => LANE_ORDER.indexOf(lane.table) + (lane.workerId && lane.workerId !== workerId && lane.table !== 'DEVICE_WORKER' ? 3 : 0)
  return {
    fromMs,
    toMs,
    band: place(bandStart, bandEnd),
    lanes: Array.from(lanes.values()).sort((a, b) => order(a) - order(b))
  }
}

const TICK_STEPS_HOURS = [1, 2, 3, 6, 12, 24, 48, 168]

/** The ticks of the axis: local hours on a step that leaves at most 8 of them; a local midnight is a day tick. */
export const ticksOf = (fromMs, toMs) => {
  const step = (TICK_STEPS_HOURS.find((hours) => (toMs - fromMs) / (hours * HOUR) <= 8) || 168) * HOUR
  const first = new Date(fromMs)
  first.setMinutes(0, 0, 0)
  const ticks = []
  for (let tick = first.getTime(); tick < toMs; tick += HOUR) {
    const date = new Date(tick)
    if (tick < fromMs) continue
    const hoursOfDay = date.getHours()
    const stepHours = step / HOUR
    if (stepHours >= 24 ? hoursOfDay === 0 && Math.round(tick / (24 * HOUR)) % (stepHours / 24) === 0 : hoursOfDay % stepHours === 0) {
      ticks.push({ ms: tick, left: ((tick - fromMs) / (toMs - fromMs)) * 100, day: hoursOfDay === 0 })
    }
  }
  return ticks
}
